import { isTaakAfgerond } from './projectMeta';
import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { T, Icon, stitchSectionLabel, stitchOutlineBtn, stitchTooltipSlotProps, StitchIconBtn } from './stitch';
import { taskTypeColors } from './projectMeta';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { formatDateISO } from '../../../utils/weekUtils';

// Vorige datums van een taakbalk (voor "Ongedaan maken" na slepen/resizen).
const vorigeDatums = (task) => ({
  StartDateUitvoerder: task?.fields?.StartDateUitvoerder || null,
  EndDateUitvoerder: task?.fields?.EndDateUitvoerder || null,
});


// Timeline View Component for Tasks
function TimelineTasksView({ tasks, deliverables = [], onTaskClick, onTaskUpdate }) {
  const [draggingTask, setDraggingTask] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [resizingTask, setResizingTask] = useState(null); // For resizing bars
  const [timeRange, setTimeRange] = useState('month'); // 'week', '2weeks', 'month', '2months', 'auto'
  const [viewStart, setViewStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7); // Start a week before today
    return d;
  });
  const timelineRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollAccumulatorRef = useRef(0);

  // Horizontal scroll → navigate through time
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || timeRange === 'auto') return;

    const handleWheel = (e) => {
      // Use deltaX for trackpad horizontal swipe, or deltaY + shift for mouse wheel
      const delta = e.deltaX !== 0 ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (delta === 0) return;

      e.preventDefault();

      // Accumulate small deltas before navigating (for smooth trackpads)
      scrollAccumulatorRef.current += delta;
      const threshold = 80;

      if (Math.abs(scrollAccumulatorRef.current) >= threshold) {
        const direction = scrollAccumulatorRef.current > 0 ? 1 : -1;
        const daysToShift = timeRange === 'week' || timeRange === '2weeks' ? 1 : 3;
        setViewStart(prev => {
          const next = new Date(prev);
          next.setDate(next.getDate() + (direction * daysToShift));
          // Skip weekends for day views to keep column count stable
          if (timeRange === 'week' || timeRange === '2weeks') {
            const day = next.getDay();
            if (day === 6) next.setDate(next.getDate() + (direction > 0 ? 2 : -1)); // Sat → Mon or Fri
            if (day === 0) next.setDate(next.getDate() + (direction > 0 ? 1 : -2)); // Sun → Mon or Fri
          }
          return next;
        });
        scrollAccumulatorRef.current = 0;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [timeRange]);

  // Range configurations
  const rangeConfig = {
    week: { days: 7, label: '1 week', step: 7 },
    '2weeks': { days: 14, label: '2 weken', step: 7 },
    month: { days: 49, label: '1 maand', step: 14 },  // 7 weeks to show more (incl March)
    '2months': { days: 70, label: '2 maanden', step: 30 },  // 10 weeks
    auto: { days: 0, label: 'Auto', step: 14 },
  };

  // Get tasks with dates, sorted by start date
  const tasksWithDates = useMemo(() => {
    return tasks
      .filter(t => t.fields?.StartDateUitvoerder || t.fields?.EndDateUitvoerder || t.fields?.Deadline)
      .sort((a, b) => {
        const dateA = a.fields?.StartDateUitvoerder || a.fields?.Deadline || '';
        const dateB = b.fields?.StartDateUitvoerder || b.fields?.Deadline || '';
        return dateA.localeCompare(dateB);
      });
  }, [tasks]);

  // Build deliverable deadline lookup: deliverableId → deadline date string
  // Also determine which task is the LAST one in each deliverable group (to show deadline only once)
  const { deliverableDeadlineMap, lastTaskPerDeliverable } = useMemo(() => {
    const deadlineMap = {};
    deliverables.forEach(del => {
      if (del.fields?.Deadline) {
        deadlineMap[del.id] = del.fields.Deadline;
      }
    });

    // For each deliverable, find the last task in tasksWithDates that belongs to it
    const lastTask = {};
    tasksWithDates.forEach(task => {
      const delIds = task.fields?.Deliverable || [];
      delIds.forEach(delId => {
        lastTask[delId] = task.id; // keep overwriting → last one wins
      });
    });

    return { deliverableDeadlineMap: deadlineMap, lastTaskPerDeliverable: lastTask };
  }, [deliverables, tasksWithDates]);

  // Calculate view range based on selection
  const { minDate, maxDate, totalDays } = useMemo(() => {
    if (timeRange === 'auto') {
      // Auto: fit all tasks
      let min = new Date();
      let max = new Date();
      max.setMonth(max.getMonth() + 1);

      tasksWithDates.forEach(task => {
        const start = task.fields?.StartDateUitvoerder ? new Date(task.fields.StartDateUitvoerder) : null;
        const end = task.fields?.EndDateUitvoerder ? new Date(task.fields.EndDateUitvoerder) : null;
        const deadline = task.fields?.Deadline ? new Date(task.fields.Deadline) : null;

        [start, end, deadline].filter(Boolean).forEach(d => {
          if (d < min) min = new Date(d);
          if (d > max) max = new Date(d);
        });
      });

      min.setDate(min.getDate() - 7);
      max.setDate(max.getDate() + 14);

      return { minDate: min, maxDate: max, totalDays: Math.ceil((max - min) / (1000 * 60 * 60 * 24)) };
    } else {
      // Fixed range from viewStart
      const min = new Date(viewStart);
      const max = new Date(viewStart);
      max.setDate(max.getDate() + rangeConfig[timeRange].days);
      return { minDate: min, maxDate: max, totalDays: rangeConfig[timeRange].days };
    }
  }, [timeRange, viewStart, tasksWithDates]);

  // Navigate timeline
  const navigateTimeline = (direction) => {
    const step = rangeConfig[timeRange].step;
    setViewStart(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + (direction * step));
      return next;
    });
  };

  const goToToday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    setViewStart(d);
  };

  // Handle drag start
  const handleDragStart = (e, task) => {
    setDraggingTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  // Handle drag over timeline area
  const handleDragOver = (e, rowElement) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop - calculate new dates
  const handleDrop = async (e, rowElement) => {
    e.preventDefault();
    if (!draggingTask) return;

    const timelineRect = rowElement.getBoundingClientRect();
    const dropX = e.clientX - timelineRect.left;
    const newStartDate = positionToDate(dropX, timelineRect.width);

    // Calculate original duration in working days (preserves duration across weekends)
    const originalStart = draggingTask.fields?.StartDateUitvoerder ? new Date(draggingTask.fields.StartDateUitvoerder) : null;
    const originalEnd = draggingTask.fields?.EndDateUitvoerder ? new Date(draggingTask.fields.EndDateUitvoerder) : null;
    let workingDaysDuration = 1;
    if (originalStart && originalEnd) {
      workingDaysDuration = Math.max(1, countWorkingDays(originalStart, originalEnd) - 1);
    }

    // Calculate new end date using working days
    const newEndDate = addWorkingDays(newStartDate, workingDaysDuration);

    // Format dates for Airtable (YYYY-MM-DD)
    const formatForAirtable = formatDateISO; // lokale dag; toISOString gaf tussen 00:00 en 02:00 de dag ervóór

    const updates = {
      StartDateUitvoerder: formatForAirtable(newStartDate),
      EndDateUitvoerder: formatForAirtable(newEndDate),
    };

    console.log(`Moving task ${draggingTask.id} to ${updates.StartDateUitvoerder} - ${updates.EndDateUitvoerder}`);

    if (onTaskUpdate) {
      const taakId = draggingTask.id;
      const vorige = vorigeDatums(draggingTask);
      const ok = await onTaskUpdate(taakId, updates);
      if (ok !== false) {
        biedOngedaanAan(`Taak verplaatst naar ${updates.StartDateUitvoerder}`, async () => {
          if ((await onTaskUpdate(taakId, vorige)) === false) throw new Error('Terugzetten mislukt');
        });
      }
    }

    setDraggingTask(null);
  };

  // Handle resize start (drag right edge of bar) - changes END date
  const handleResizeStartRight = (e, task) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingTask(task);

    const handleMouseMove = (moveEvent) => {
      // Visual feedback could be added here
    };

    const handleMouseUp = async (upEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (!scrollContainerRef.current) { setResizingTask(null); return; }
      const timelineRow = scrollContainerRef.current.querySelector(`[data-task-id="${task.id}"]`);
      if (!timelineRow) { setResizingTask(null); return; }

      const timelineRect = timelineRow.getBoundingClientRect();
      const mouseX = upEvent.clientX - timelineRect.left;
      const newEndDate = positionToDate(mouseX, timelineRect.width);

      const formatForAirtable = formatDateISO; // lokale dag; toISOString gaf tussen 00:00 en 02:00 de dag ervóór
      const updates = { EndDateUitvoerder: formatForAirtable(newEndDate) };

      console.log(`Resizing task ${task.id} end date to ${updates.EndDateUitvoerder}`);
      if (onTaskUpdate) {
        const vorige = vorigeDatums(task);
        const ok = await onTaskUpdate(task.id, updates);
        if (ok !== false) {
          biedOngedaanAan(`Einddatum naar ${updates.EndDateUitvoerder}`, async () => {
            if ((await onTaskUpdate(task.id, vorige)) === false) throw new Error('Terugzetten mislukt');
          });
        }
      }
      setResizingTask(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle resize start (drag left edge of bar) - changes START date
  const handleResizeStartLeft = (e, task) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingTask(task);

    const handleMouseMove = (moveEvent) => {
      // Visual feedback could be added here
    };

    const handleMouseUp = async (upEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (!scrollContainerRef.current) { setResizingTask(null); return; }
      const timelineRow = scrollContainerRef.current.querySelector(`[data-task-id="${task.id}"]`);
      if (!timelineRow) { setResizingTask(null); return; }

      const timelineRect = timelineRow.getBoundingClientRect();
      const mouseX = upEvent.clientX - timelineRect.left;
      const newStartDate = positionToDate(mouseX, timelineRect.width);

      const formatForAirtable = formatDateISO; // lokale dag; toISOString gaf tussen 00:00 en 02:00 de dag ervóór
      const updates = { StartDateUitvoerder: formatForAirtable(newStartDate) };

      console.log(`Resizing task ${task.id} start date to ${updates.StartDateUitvoerder}`);
      if (onTaskUpdate) {
        const vorige = vorigeDatums(task);
        const ok = await onTaskUpdate(task.id, updates);
        if (ok !== false) {
          biedOngedaanAan(`Startdatum naar ${updates.StartDateUitvoerder}`, async () => {
            if ((await onTaskUpdate(task.id, vorige)) === false) throw new Error('Terugzetten mislukt');
          });
        }
      }

      setResizingTask(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Generate time columns (days for short ranges, weeks for longer)
  // Skip weekends for day view
  const columns = useMemo(() => {
    const result = [];
    const current = new Date(minDate);

    if (timeRange === 'week' || timeRange === '2weeks') {
      // Show days for short ranges (skip weekends)
      while (current <= maxDate) {
        const dayOfWeek = current.getDay();
        // Skip Saturday (6) and Sunday (0)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          result.push({ date: new Date(current), type: 'day' });
        }
        current.setDate(current.getDate() + 1);
      }
    } else {
      // Show weeks for longer ranges
      current.setDate(current.getDate() - current.getDay() + 1); // Start from Monday
      while (current <= maxDate) {
        result.push({ date: new Date(current), type: 'week' });
        current.setDate(current.getDate() + 7);
      }
    }
    return result;
  }, [minDate, maxDate, timeRange]);

  // Backwards compat alias
  const weeks = columns;

  // Helper: count working days between two dates (can return negative)
  const countWorkingDays = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const direction = from <= to ? 1 : -1;
    let count = 0;
    const current = new Date(direction === 1 ? from : to);
    const endDate = new Date(direction === 1 ? to : from);

    while (current <= endDate) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    return count * direction;
  };

  // Helper: add N working days to a date (skips weekends)
  const addWorkingDays = (fromDate, numDays) => {
    const result = new Date(fromDate);
    let added = 0;
    const direction = numDays >= 0 ? 1 : -1;
    const target = Math.abs(numDays);
    while (added < target) {
      result.setDate(result.getDate() + direction);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return result;
  };

  // Helper: convert drop position (pixel X within bar area) to a date
  const positionToDate = (dropX, barWidth) => {
    const dropPercent = Math.max(0, Math.min(1, dropX / barWidth));
    if (timeRange === 'week' || timeRange === '2weeks') {
      // Snap to nearest column (working day)
      const colIndex = Math.min(columns.length - 1, Math.floor(dropPercent * columns.length));
      return new Date(columns[colIndex].date);
    } else {
      // Use column grid start (Monday) for consistency with bar rendering
      const gridStart = columns.length > 0 ? columns[0].date : minDate;
      const totalGridDays = columns.length * 7 || 1;
      const dayOffset = Math.round(dropPercent * totalGridDays);
      const date = new Date(gridStart);
      date.setDate(date.getDate() + dayOffset);
      return date;
    }
  };

  // Calculate bar position for a task
  const getTaskBar = (task) => {
    const start = task.fields?.StartDateUitvoerder ? new Date(task.fields.StartDateUitvoerder) : null;
    const end = task.fields?.EndDateUitvoerder ? new Date(task.fields.EndDateUitvoerder) : null;
    const deadline = task.fields?.Deadline ? new Date(task.fields.Deadline) : null;

    // Deadline-only task: has deadline but NO start AND NO end dates
    // Also treat Type="Deadline" as deadline marker, or task name starts with "Deadline:"
    const taskType = task.fields?.Type || '';
    const taskName = task.fields?.NaamTaak || '';
    const isDeadlineOnly = (!start && !end && deadline) ||
                           (taskType === 'Deadline') ||
                           taskName.toLowerCase().startsWith('deadline');

    if (!start && !end && !deadline) return null;

    // For deadline-only: position at deadline date
    // For regular tasks: use start/end
    const effectiveStart = isDeadlineOnly ? (deadline || start) : (start || deadline);
    const effectiveEnd = isDeadlineOnly ? (deadline || start) : (end || start || deadline);

    // Safety check
    if (!effectiveStart) return null;

    // Check if task is completely outside visible range
    // Task ended BEFORE the visible range starts
    const isBeforeRange = effectiveEnd < minDate;
    // Task starts AFTER the visible range ends
    const isAfterRange = effectiveStart > maxDate;

    // For day view (week/2weeks), use working days calculation
    if (timeRange === 'week' || timeRange === '2weeks') {
      const totalWorkingDays = columns.length || 1;
      if (!minDate) return null;

      // Calculate offset from minDate to task start (can be negative)
      const startOffsetDays = countWorkingDays(minDate, effectiveStart) - 1;
      const endOffsetDays = countWorkingDays(minDate, effectiveEnd);

      // Clamp to visible range for bar rendering
      const visibleStart = Math.max(0, startOffsetDays);
      const visibleEnd = Math.min(totalWorkingDays, endOffsetDays);
      const visibleDuration = isDeadlineOnly ? 1 : Math.max(0, visibleEnd - visibleStart);

      // For deadline markers: center in column (+0.5); for bars: left edge of column
      const leftPercent = ((visibleStart + (isDeadlineOnly ? 0.5 : 0)) / totalWorkingDays) * 100;
      const widthPercent = isDeadlineOnly ? 2 : Math.max(0, (visibleDuration / totalWorkingDays) * 100);

      return {
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        isDeadlineOnly,
        isBeforeRange,
        isAfterRange,
      };
    }

    // For longer ranges (month/2months/auto), align bars to column grid
    // Columns start from Monday of minDate's week, so use that as reference
    const gridStart = columns.length > 0 ? columns[0].date : minDate;
    const totalGridDays = columns.length * 7 || 1;
    const startOffsetDays = Math.floor((effectiveStart - gridStart) / (1000 * 60 * 60 * 24));
    const endOffsetDays = Math.ceil((effectiveEnd - gridStart) / (1000 * 60 * 60 * 24));

    // Clamp to visible range
    const visibleStart = Math.max(0, startOffsetDays);
    const visibleEnd = Math.min(totalGridDays, endOffsetDays + 1);
    const visibleDuration = isDeadlineOnly ? 1 : Math.max(0, visibleEnd - visibleStart);

    const leftPercent = ((visibleStart + (isDeadlineOnly ? 0.5 : 0)) / totalGridDays) * 100;
    const widthPercent = isDeadlineOnly ? 2 : Math.max(0, (visibleDuration / totalGridDays) * 100);

    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
      isDeadlineOnly,
      isBeforeRange,
      isAfterRange,
    };
  };

  // Get week number from date
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const formatColumn = (col) => {
    if (col.type === 'day') {
      const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
      return `${dayNames[col.date.getDay()]} ${col.date.getDate()}`;
    }
    // Show week number + start-end date for week columns
    const weekNum = getWeekNumber(col.date);
    const endOfWeek = new Date(col.date);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    const startDay = col.date.getDate();
    const startMonth = col.date.toLocaleDateString('nl-NL', { month: 'short' });
    const endDay = endOfWeek.getDate();
    const endMonth = endOfWeek.toLocaleDateString('nl-NL', { month: 'short' });
    const dateRange = startMonth === endMonth
      ? `${startDay}-${endDay} ${startMonth}`
      : `${startDay} ${startMonth}-${endDay} ${endMonth}`;
    return `W${weekNum} \u00B7 ${dateRange}`;
  };

  // Legacy alias
  const formatWeek = (date) => date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });

  if (tasksWithDates.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Geen taken met datums gevonden</Typography>
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
          Voeg start- of einddatums toe aan taken om ze in de timeline te zien
        </Typography>
      </Box>
    );
  }

  const timeRangeOptions = [
    { value: 'week', label: '1 week' },
    { value: '2weeks', label: '2 weken' },
    { value: 'month', label: '1 maand' },
    { value: '2months', label: '2 maanden' },
    { value: 'auto', label: 'Auto' },
  ];

  return (
    <Box sx={{ overflow: 'hidden', bgcolor: T.surface, boxShadow: T.shadow, borderRadius: '0.75rem' }}>
      {/* Timeline Controls */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1,
        borderBottom: `1px solid ${T.surfaceHigh}`,
        backgroundColor: T.surface,
      }}>
        {/* Range Selector - pill style */}
        <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: T.surfaceLow, borderRadius: '9999px' }}>
          {timeRangeOptions.map(opt => (
            <Box
              key={opt.value}
              onClick={() => setTimeRange(opt.value)}
              sx={{
                px: 1.5, py: 0.5,
                borderRadius: '9999px',
                fontFamily: T.fontBody,
                fontSize: '0.75rem',
                fontWeight: timeRange === opt.value ? 700 : 500,
                color: timeRange === opt.value ? '#fff' : T.secondary,
                bgcolor: timeRange === opt.value ? T.primary : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': timeRange === opt.value ? {} : { bgcolor: T.surfaceHigh },
              }}
            >
              {opt.label}
            </Box>
          ))}
        </Box>

        {/* Navigation */}
        {timeRange !== 'auto' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StitchIconBtn onClick={() => navigateTimeline(-1)} title="Vorige">
              <Icon name="arrow_back" size={18} />
            </StitchIconBtn>
            <Box
              onClick={goToToday}
              sx={{ ...stitchOutlineBtn, px: 1.5, py: 0.4, fontSize: '0.75rem' }}
            >
              Vandaag
            </Box>
            <StitchIconBtn onClick={() => navigateTimeline(1)} title="Volgende">
              <Icon name="arrow_forward" size={18} />
            </StitchIconBtn>
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, ml: 1 }}>
              {minDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} - {maxDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Scrollable Timeline Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          maxHeight: 550,
          overflowX: 'hidden',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {/* Timeline Header - Sticky */}
        <Box sx={{
          display: 'flex',
          borderBottom: `1px solid ${T.surfaceHigh}`,
          backgroundColor: T.surfaceLow,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          minWidth: 'fit-content',
        }}>
          {/* Sticky Header Cell - Left */}
          <Box sx={{
            width: 180,
            minWidth: 180,
            flexShrink: 0,
            py: 0.5,
            px: 0.75,
            borderRight: `2px solid ${T.surfaceHigh}`,
            position: 'sticky',
            left: 0,
            backgroundColor: T.surfaceLow,
            zIndex: 11,
            boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
          }}>
            <Typography variant="caption" sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>TAAK</Typography>
          </Box>
          {/* Timeline columns - fixed widths for alignment */}
          <Box sx={{ display: 'flex', flexShrink: 0, backgroundColor: T.surfaceLow, width: columns.length * (timeRange === 'week' ? 100 : timeRange === '2weeks' ? 80 : 110) }}>
            {columns.map((col, idx) => {
              const colWidth = timeRange === 'week' ? 100 : timeRange === '2weeks' ? 80 : 110;
              const isToday = col.type === 'day'
                ? col.date.toDateString() === new Date().toDateString()
                : (new Date() >= col.date && new Date() < new Date(col.date.getTime() + 7 * 24 * 60 * 60 * 1000));
              const isWeekend = col.type === 'day' && (col.date.getDay() === 0 || col.date.getDay() === 6);

              return (
                <Box
                  key={idx}
                  sx={{
                    width: colWidth,
                    minWidth: colWidth,
                    maxWidth: colWidth,
                    py: 0.5,
                    px: 0.25,
                    textAlign: 'center',
                    borderRight: `1px solid ${T.outlineVariant}`,
                    backgroundColor: isToday ? T.primaryContainer : isWeekend ? T.surfaceLow : T.surfaceLow,
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, fontWeight: isToday ? 700 : 500, fontSize: '0.6rem' }}>
                    {formatColumn(col)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {/* Sticky Header Cell - Right (Deadline) */}
          <Box sx={{
            width: 90,
            minWidth: 90,
            flexShrink: 0,
            py: 0.5,
            px: 0.75,
            borderLeft: `2px solid ${T.surfaceHigh}`,
            position: 'sticky',
            right: 0,
            backgroundColor: T.surfaceLow,
            zIndex: 11,
            boxShadow: '-2px 0 4px rgba(0,0,0,0.08)',
            textAlign: 'center',
          }}>
            <Typography variant="caption" sx={{ ...stitchSectionLabel, fontSize: '0.65rem' }}>DEADLINE</Typography>
          </Box>
        </Box>

        {/* Timeline Rows */}
        {tasksWithDates.map((task) => {
          const bar = getTaskBar(task);
          const type = task.fields?.Type || '';
          const isComplete = isTaakAfgerond(task);
          // Get first name only, avoid duplicates
          const rawAssignee = task.fields?.['Voornaam Assignee']?.[0] || '';
          const assignee = rawAssignee.includes('(') ? rawAssignee.split('(')[0].trim() : rawAssignee;
          const isResizing = resizingTask?.id === task.id;

          // Get dates for display - use Deadline as fallback
          const startDate = task.fields?.StartDateUitvoerder;
          const endDate = task.fields?.EndDateUitvoerder;
          const deadline = task.fields?.Deadline;
          const displayDate = startDate || deadline;
          const hasDateRange = startDate && endDate && startDate !== endDate;

          // Deliverable deadline: look up via task's Deliverable link
          const taskDelIds = task.fields?.Deliverable || [];
          const deliverableDeadline = taskDelIds.length > 0 ? deliverableDeadlineMap[taskDelIds[0]] : null;
          // Only show deliverable deadline on the LAST task of that deliverable
          const isLastTaskOfDeliverable = taskDelIds.length > 0 && lastTaskPerDeliverable[taskDelIds[0]] === task.id;

          // Format short date (only day and month) - with safety checks
          const formatShortDate = (d) => {
            if (!d) return '';
            try {
              const date = new Date(d);
              if (isNaN(date.getTime())) return ''; // Invalid date
              return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
            } catch (e) {
              return '';
            }
          };

          return (
            <Box
              key={task.id}
              sx={{
                display: 'flex',
                borderBottom: `1px solid ${T.surfaceHigh}`,
                minHeight: 32,
                minWidth: 'fit-content',
                '&:hover': { backgroundColor: T.surfaceLow },
              }}
            >
              {/* Task Name - Sticky Left */}
              <Box
                sx={{
                  width: 180,
                  minWidth: 180,
                  flexShrink: 0,
                  py: 0.5,
                  px: 0.75,
                  borderRight: `2px solid ${T.surfaceHigh}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  position: 'sticky',
                  left: 0,
                  backgroundColor: T.surface,
                  zIndex: 5,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                }}
                onClick={() => onTaskClick?.(task)}
              >
                {isComplete ? (
                  <Icon name="check_circle" size={14} sx={{ color: T.emerald, flexShrink: 0 }} />
                ) : (
                  <Icon name="radio_button_unchecked" size={14} sx={{ color: T.outlineVariant, flexShrink: 0 }} />
                )}
                <Box sx={{ overflow: 'hidden', flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: T.fontBody,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textDecoration: isComplete ? 'line-through' : 'none',
                      color: isComplete ? T.outlineVariant : T.text,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      display: 'block',
                    }}
                  >
                    {(task.fields?.NaamTaak || '').split(' - ')[0]}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {assignee && (
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, fontSize: '0.6rem', lineHeight: 1 }}>
                        {assignee}
                      </Typography>
                    )}
                    {assignee && displayDate && <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, fontSize: '0.5rem' }}>{'\u2022'}</Typography>}
                    {displayDate && (
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: startDate ? '#6366f1' : T.error, fontSize: '0.6rem', fontWeight: 500, lineHeight: 1 }}>
                        {hasDateRange
                          ? `${formatShortDate(startDate)} \u2192 ${formatShortDate(endDate)}`
                          : !startDate && deadline
                            ? `\u2691 ${formatShortDate(deadline)}`
                            : formatShortDate(displayDate)
                        }
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Timeline Bar Area with Grid */}
              <Box
                data-task-id={task.id}
                ref={timelineRef}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'stretch',
                  flexShrink: 0,
                  minHeight: 32,
                  width: columns.length * (timeRange === 'week' ? 100 : timeRange === '2weeks' ? 80 : 110),
                  minWidth: columns.length * (timeRange === 'week' ? 100 : timeRange === '2weeks' ? 80 : 110),
                  overflow: 'visible', // Allow bar labels to extend beyond columns
                }}
                onDragOver={(e) => handleDragOver(e, e.currentTarget)}
                onDrop={(e) => handleDrop(e, e.currentTarget)}
              >
                {/* Grid columns background */}
                {columns.map((col, idx) => {
                  const colWidth = timeRange === 'week' ? 100 : timeRange === '2weeks' ? 80 : 110;
                  const isToday = col.type === 'day'
                    ? col.date.toDateString() === new Date().toDateString()
                    : (new Date() >= col.date && new Date() < new Date(col.date.getTime() + 7 * 24 * 60 * 60 * 1000));
                  return (
                    <Box
                      key={idx}
                      sx={{
                        width: colWidth,
                        minWidth: colWidth,
                        maxWidth: colWidth,
                        height: '100%',
                        minHeight: 32,
                        borderRight: `1px solid ${T.outlineVariant}`,
                        backgroundColor: isToday ? 'rgba(99, 102, 241, 0.08)' : col.type === 'day' && (col.date.getDay() === 0 || col.date.getDay() === 6) ? T.surfaceLow : 'transparent',
                        boxSizing: 'border-box',
                      }}
                    />
                  );
                })}
                {bar && (
                  <>
                    {/* Task already ended (before visible range) - show left arrow */}
                    {bar.isBeforeRange && (
                      <Tooltip title={`Taak eindigde ${formatShortDate(endDate || deadline)}`} slotProps={stitchTooltipSlotProps}>
                        <Box
                          onClick={() => onTaskClick?.(task)}
                          sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.7rem',
                            fontFamily: T.fontBody,
                            color: T.outlineVariant,
                            cursor: 'pointer',
                            zIndex: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <span style={{ color: T.outlineVariant }}>{'\u25C0'}</span> {formatShortDate(endDate || deadline)}
                        </Box>
                      </Tooltip>
                    )}

                    {/* Task starts after visible range - show right arrow */}
                    {bar.isAfterRange && (
                      <Tooltip title={`Taak start ${formatShortDate(startDate || deadline)}`} slotProps={stitchTooltipSlotProps}>
                        <Box
                          onClick={() => onTaskClick?.(task)}
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '0.7rem',
                            fontFamily: T.fontBody,
                            color: '#6366f1',
                            cursor: 'pointer',
                            zIndex: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {formatShortDate(startDate || deadline)} <span style={{ color: '#6366f1' }}>{'\u25B6'}</span>
                        </Box>
                      </Tooltip>
                    )}

                    {/* Task is (partially) in visible range - show bar or diamond */}
                    {!bar.isBeforeRange && !bar.isAfterRange && (
                      bar.isDeadlineOnly ? (
                        // Diamond marker for deadline-only tasks
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }}>{task.fields?.NaamTaak || 'Taak'}</Typography>
                              <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>Deadline: {formatShortDate(deadline)}</Typography>
                            </Box>
                          }
                          arrow
                          slotProps={stitchTooltipSlotProps}
                        >
                          <Box
                            onClick={() => onTaskClick?.(task)}
                            sx={{
                              position: 'absolute',
                              left: bar.left,
                              top: '50%',
                              transform: 'translate(-50%, -50%) rotate(45deg)',
                              width: 14,
                              height: 14,
                              backgroundColor: T.error,
                              borderRadius: 0.5,
                              cursor: 'pointer',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                              zIndex: 2,
                              '&:hover': {
                                filter: 'brightness(0.9)',
                                transform: 'translate(-50%, -50%) rotate(45deg) scale(1.2)',
                              },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        // Duration bar for tasks with start/end dates
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }}>{task.fields?.NaamTaak || 'Taak'}</Typography>
                              <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>
                                {formatShortDate(startDate)} {'\u2192'} {formatShortDate(endDate)}
                              </Typography>
                              {assignee && <Typography variant="caption" sx={{ fontFamily: T.fontBody }} display="block">{assignee}</Typography>}
                            </Box>
                          }
                          arrow
                          slotProps={stitchTooltipSlotProps}
                        >
                          <Box
                            draggable
                            onDragStart={(e) => handleDragStart(e, task)}
                            onDragEnd={() => setDraggingTask(null)}
                            onClick={() => onTaskClick?.(task)}
                            sx={{
                              position: 'absolute',
                              left: bar.left,
                              width: bar.width,
                              top: 4,
                              bottom: 4,
                              minWidth: 20,
                              backgroundColor: isComplete ? T.outlineVariant : (taskTypeColors[type] || '#3b82f6'),
                              opacity: draggingTask?.id === task.id || isResizing ? 0.7 : 1,
                              borderRadius: 0.5,
                              cursor: 'grab',
                              transition: isResizing ? 'none' : 'all 0.15s',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                              zIndex: 1,
                              '&:hover': {
                                filter: 'brightness(0.95)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              },
                              '&:active': { cursor: 'grabbing' },
                            }}
                          >
                            {/* Left resize handle */}
                            <Box
                              onMouseDown={(e) => handleResizeStartLeft(e, task)}
                              sx={{
                                position: 'absolute', left: 0, top: 0, bottom: 0, width: 6,
                                cursor: 'ew-resize', borderRadius: '4px 0 0 4px',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                              }}
                            />
                            {/* Right resize handle */}
                            <Box
                              onMouseDown={(e) => handleResizeStartRight(e, task)}
                              sx={{
                                position: 'absolute', right: 0, top: 0, bottom: 0, width: 6,
                                cursor: 'ew-resize', borderRadius: '0 4px 4px 0',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                              }}
                            />
                          </Box>
                        </Tooltip>
                      )
                    )}

                    {/* Task name in black text after the bar - only for in-range tasks */}
                    {!bar.isBeforeRange && !bar.isAfterRange && (
                      <Typography
                        variant="caption"
                        onClick={() => onTaskClick?.(task)}
                        sx={{
                          fontFamily: T.fontBody,
                          position: 'absolute',
                          left: `calc(${bar.left} + ${bar.isDeadlineOnly ? '12px' : bar.width} + 4px)`,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: isComplete ? T.outlineVariant : T.text,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          textDecoration: isComplete ? 'line-through' : 'none',
                          zIndex: 3,
                          '&:hover': { color: T.text },
                        }}
                      >
                        {(task.fields?.NaamTaak || '').split(' - ')[0]}
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              {/* Deadline Column - Sticky Right */}
              <Box
                sx={{
                  width: 90,
                  minWidth: 90,
                  flexShrink: 0,
                  py: 0.5,
                  px: 0.75,
                  borderLeft: `2px solid ${T.surfaceHigh}`,
                  position: 'sticky',
                  right: 0,
                  backgroundColor: T.surface,
                  zIndex: 5,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isLastTaskOfDeliverable && deliverableDeadline && (
                  <Tooltip title={`Deliverable deadline: ${formatShortDate(deliverableDeadline)}`} slotProps={stitchTooltipSlotProps}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          backgroundColor: T.error,
                          transform: 'rotate(45deg)',
                          borderRadius: 0.5,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: T.fontBody,
                          fontSize: '0.65rem',
                          fontWeight: 500,
                          color: T.error,
                        }}
                      >
                        {formatShortDate(deliverableDeadline)}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default TimelineTasksView;
